import { createPortal } from "react-dom";
import { useEffect, useId, useRef, useState } from "react";
import { cn } from "../utils/cn";

// ── Shared scroll lock (same dataset key as Modal for cooperative locking) ────

function lockBodyScroll() {
  const n = parseInt(document.body.dataset.scrollLocks ?? "0", 10);
  if (n === 0) {
    const bar = window.innerWidth - document.documentElement.clientWidth;
    document.body.style.overflow = "hidden";
    if (bar > 0) document.body.style.paddingRight = `${bar}px`;
  }
  document.body.dataset.scrollLocks = String(n + 1);
}

function unlockBodyScroll() {
  const n = Math.max(0, parseInt(document.body.dataset.scrollLocks ?? "0", 10) - 1);
  document.body.dataset.scrollLocks = String(n);
  if (n === 0) {
    document.body.style.overflow = "";
    document.body.style.paddingRight = "";
  }
}

// ── Focus trap ────────────────────────────────────────────────────────────────

const FOCUSABLE =
  'a[href]:not([tabindex="-1"]),' +
  'button:not([disabled]):not([tabindex="-1"]),' +
  'textarea:not([disabled]):not([tabindex="-1"]),' +
  'input:not([disabled]):not([tabindex="-1"]),' +
  'select:not([disabled]):not([tabindex="-1"]),' +
  '[tabindex]:not([tabindex="-1"])';

// ── Icons ─────────────────────────────────────────────────────────────────────

function XIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
      stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  );
}

// ── Types ─────────────────────────────────────────────────────────────────────

export interface DrawerProps {
  /** Whether the drawer is currently shown */
  isOpen: boolean;
  /** Called when the user requests the drawer to close */
  onClose: () => void;
  /** Heading rendered at the top of the drawer */
  title?: string;
  /** Which edge the drawer slides from */
  position?: "left" | "right";
  /** Controls the width of the panel */
  size?: "sm" | "md" | "lg";
  /** Content rendered in the sticky footer — typically action buttons */
  footer?: React.ReactNode;
  children: React.ReactNode;
}

// ── Size map ──────────────────────────────────────────────────────────────────

const PANEL_WIDTH = {
  sm: "w-[320px]",
  md: "w-[480px]",
  lg: "w-[640px]",
};

// ── Component ─────────────────────────────────────────────────────────────────

export function Drawer({
  isOpen,
  onClose,
  title,
  position = "right",
  size = "md",
  footer,
  children,
}: DrawerProps) {
  const id = useId();
  const panelRef = useRef<HTMLDivElement>(null);

  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setMounted(true);
      lockBodyScroll();
      const frame = requestAnimationFrame(() => setVisible(true));
      return () => {
        cancelAnimationFrame(frame);
        unlockBodyScroll();
      };
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Focus trap + Escape
  useEffect(() => {
    if (!visible || !panelRef.current) return;

    const els = () =>
      Array.from(panelRef.current!.querySelectorAll<HTMLElement>(FOCUSABLE));

    els()[0]?.focus();

    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const focusable = els();
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [visible, onClose]);

  if (!mounted || typeof document === "undefined") return null;

  const isRight = position === "right";

  return createPortal(
    <div
      className={cn(
        "fixed inset-0 z-[1040] flex",
        isRight ? "justify-end" : "justify-start",
      )}
      role="presentation"
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        className={cn(
          "absolute inset-0 bg-black/50 backdrop-blur-sm",
          "transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />

      {/* Panel */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? `${id}-title` : undefined}
        tabIndex={-1}
        className={cn(
          "relative z-[1050] flex flex-col h-full outline-none",
          "bg-surface dark:bg-surface-raised",
          "transition-transform duration-300 ease-out",
          visible
            ? "translate-x-0"
            : isRight
              ? "translate-x-full"
              : "-translate-x-full",
          PANEL_WIDTH[size],
        )}
        style={{ boxShadow: "var(--shadow-modal)" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between shrink-0 px-6 py-4 border-b border-border-default dark:border-border-strong">
          {title && (
            <h2
              id={`${id}-title`}
              className="text-lg font-semibold text-content-primary"
            >
              {title}
            </h2>
          )}
          <button
            type="button"
            aria-label="Close drawer"
            onClick={onClose}
            className={cn(
              "ml-auto p-1.5 rounded-md transition-colors duration-150",
              "text-content-tertiary hover:text-content-primary",
              "hover:bg-surface-overlay dark:hover:bg-surface-overlay",
              "focus-visible:outline-none focus-visible:ring-2",
              "focus-visible:ring-brand-purple focus-visible:ring-offset-2",
              "focus-visible:ring-offset-surface",
            )}
          >
            <XIcon />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="shrink-0 flex items-center justify-end gap-3 px-6 py-4 border-t border-border-default dark:border-border-strong">
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

Drawer.displayName = "Drawer";
